import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
    Divider, IconButton, Stack, Switch, Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import ComputerRoundedIcon from "@mui/icons-material/ComputerRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import dayjs from "dayjs";

import QrCode from "../../component/common/QrCode";
import { api } from "../../util/api";
import {
    getLocalSubscription, subscribeThisDevice, unsubscribeThisDevice,
} from "../../util/push/pushClient";
import { checkPushSupport, describeThisDevice, SUPPORT } from "../../util/push/browserSupport";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

/** 등록 감지 폴링 간격 — 폰에서 절차를 마치는 데 보통 30초~2분 걸린다. */
const POLL_MS = 3000;

/**
 * 휴대폰·PC 알림 설정 모달 (PC 에서 연다).
 *
 * <h3>QR 이 하는 일</h3>
 * <p>폰에서 사번·비밀번호를 타이핑하지 않게 하는 것. 그게 셋업 마찰의 대부분이다.
 * PC 는 이미 로그인돼 있으므로 1회용 페어링 코드를 발급해 QR 로 넘기고, 폰은 그 코드로
 * 구독만 등록한다 — <b>세션은 발급되지 않는다.</b>
 *
 * <h3>등록되면 화면이 즉시 ✅ 로 바뀐다</h3>
 * <p>이게 없으면 "된 건가요?" 를 물어보러 온다. 3초마다 상태를 확인해 확인 즉시 반영한다.
 *
 * <h3>모달을 닫으면 코드를 폐기한다</h3>
 * <p>유효한 코드가 화면 밖에서 살아 있으면 안 된다 — 그 값이 곧 "이 계정에 구독을 붙일 권한"이다.
 */
export default function PushSetupModal({ open, onClose }) {
    const [support] = useState(() => checkPushSupport());
    const [enabled, setEnabled] = useState(null);       // 서버가 푸시를 쓸 수 있는 상태인가
    const [thisDeviceOn, setThisDeviceOn] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const [devices, setDevices] = useState([]);
    const [pairing, setPairing] = useState(null);        // { code, expiresAt }
    const [paired, setPaired] = useState(false);
    const pollRef = useRef(null);

    const loadDevices = useCallback(async () => {
        try {
            const res = await api.get(`${API_BASE}/push/devices`);
            setDevices(Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []));
        } catch { /* 목록은 부가 정보 — 실패해도 등록 흐름을 막지 않는다 */ }
    }, []);

    // 열릴 때: 서버 가용성 + 이 브라우저 구독 상태 + 기기 목록
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get(`${API_BASE}/push/status`);
                const data = res?.data ?? res ?? {};
                if (!cancelled) setEnabled(!!data.enabled);
            } catch { if (!cancelled) setEnabled(false); }

            const local = await getLocalSubscription();
            if (!cancelled) setThisDeviceOn(!!local);
            if (!cancelled) loadDevices();
        })();
        return () => { cancelled = true; };
    }, [open, loadDevices]);

    /** QR 발급 — 모달을 열었다고 바로 만들지 않는다. 필요할 때만 만들어 유효 코드 수를 줄인다. */
    const issuePairing = async () => {
        setError(null);
        setPaired(false);
        try {
            const res = await api.post(`${API_BASE}/push/pairing`, {});
            const data = res?.data ?? res ?? {};
            setPairing({ code: data.code, expiresAt: data.expiresAt });
        } catch (e) {
            setError(readError(e, "QR 코드를 만들 수 없습니다."));
        }
    };

    /** 등록 감지 폴링 — 코드가 소진되면 등록된 것이다. */
    useEffect(() => {
        if (!pairing?.code || paired) return undefined;
        const tick = async () => {
            try {
                const res = await api.get(`${API_BASE}/push/pairing/${pairing.code}/status`);
                const data = res?.data ?? res ?? {};
                if (data.registered) {
                    setPaired(true);
                    setPairing(null);
                    loadDevices();
                }
            } catch { /* 만료로 404 가 날 수 있다 — 아래 만료 표시가 처리한다 */ }
        };
        pollRef.current = setInterval(tick, POLL_MS);
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [pairing, paired, loadDevices]);

    /** 닫을 때 코드 폐기 — 화면 밖에 유효한 코드를 남기지 않는다. */
    const handleClose = async () => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (pairing?.code) {
            try { await api.delete(`${API_BASE}/push/pairing/${pairing.code}`); } catch {}
        }
        setPairing(null);
        setPaired(false);
        setError(null);
        onClose();
    };

    const toggleThisDevice = async (next) => {
        setBusy(true);
        setError(null);
        try {
            if (next) {
                await subscribeThisDevice();
                setThisDeviceOn(true);
            } else {
                await unsubscribeThisDevice();
                setThisDeviceOn(false);
            }
            await loadDevices();
        } catch (e) {
            setError(e?.message || "알림 설정에 실패했습니다.");
        } finally {
            setBusy(false);
        }
    };

    const removeDevice = async (id) => {
        try {
            await api.delete(`${API_BASE}/push/devices/${id}`);
            await loadDevices();
            // 지운 게 이 브라우저였을 수 있다 — 로컬 상태를 다시 읽어 어긋남을 막는다.
            const local = await getLocalSubscription();
            setThisDeviceOn(!!local);
        } catch (e) {
            setError(readError(e, "기기를 해지할 수 없습니다."));
        }
    };

    const pcSupported = support === SUPPORT.OK;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
                slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 800, fontSize: 16 }}>실시간 알림 등록</Typography>
                    <IconButton size="small" onClick={handleClose}><CloseRoundedIcon fontSize="small" /></IconButton>
                </Stack>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5 }}>
                    결재·메일·멘션이 오면 잠금화면으로 알려드립니다. 내용 확인은 PC 에서 하세요.
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                {enabled === false && (
                    <Notice tone="warn">
                        서버에 알림 설정이 아직 되어 있지 않습니다. 관리자에게 문의해 주세요.
                    </Notice>
                )}

                {error && <Notice tone="error">{error}</Notice>}

                {/* ── 이 PC ───────────────────────────────────────────── */}
                <Stack direction="row" alignItems="center" spacing={1.5}
                       sx={{ py: 1.5, opacity: enabled === false ? 0.5 : 1 }}>
                    <ComputerRoundedIcon sx={{ color: "#64748b" }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>이 PC 에서 받기</Typography>
                        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                            {describeThisDevice()}
                            {!pcSupported && " — 이 브라우저는 지원하지 않습니다"}
                        </Typography>
                    </Box>
                    {busy
                        ? <CircularProgress size={18} />
                        : <Switch checked={thisDeviceOn} disabled={!pcSupported || enabled === false}
                                  onChange={(e) => toggleThisDevice(e.target.checked)} />}
                </Stack>

                <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 1 }}>
                    크롬을 완전히 종료하면 그동안은 오지 않고, 다시 켜면 밀린 알림이 옵니다.
                </Typography>

                <Divider sx={{ my: 1.5 }} />

                {/* ── 휴대폰 (QR) ─────────────────────────────────────── */}
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <PhoneIphoneRoundedIcon sx={{ color: "#64748b" }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>휴대폰에서 받기</Typography>
                        <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                            QR 을 폰 카메라로 찍으면 됩니다. 로그인하지 않아도 됩니다.
                        </Typography>
                    </Box>
                </Stack>

                {paired && (
                    <Notice tone="ok">
                        <Stack direction="row" spacing={0.8} alignItems="center">
                            <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                            <span>휴대폰 등록이 완료되었습니다.</span>
                        </Stack>
                    </Notice>
                )}

                {!pairing && !paired && (
                    <Button fullWidth variant="outlined" onClick={issuePairing}
                            disabled={enabled === false}
                            sx={{ mb: 1 }}>
                        QR 코드 만들기
                    </Button>
                )}

                {pairing && (
                    <Stack alignItems="center" spacing={1} sx={{ py: 1 }}>
                        <Box sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#fff" }}>
                            <QrCode value={pairingUrl(pairing.code)} size={200} alt="휴대폰 알림 등록 QR" />
                        </Box>
                        <Stack direction="row" spacing={0.8} alignItems="center">
                            <CircularProgress size={12} />
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                                폰에서 등록을 기다리는 중… ({expiryText(pairing.expiresAt)})
                            </Typography>
                        </Stack>
                        <Typography sx={{ fontSize: 11, color: "text.disabled", textAlign: "center" }}>
                            <b>안드로이드</b>는 찍고 버튼 한 번이면 끝입니다.
                            <br />
                            <b>아이폰</b>은 <b>Safari</b> 로 찍은 뒤 <b>공유 → 홈 화면에 추가</b> 하고,
                            그 아이콘으로 앱을 열어 <b>로그인 후</b> 알림을 켜야 합니다
                            (아이콘은 이 QR 페이지가 아니라 앱 첫 화면을 엽니다).
                        </Typography>
                        <Button size="small" onClick={issuePairing} sx={{ fontSize: 11.5 }}>
                            새 QR 만들기
                        </Button>
                    </Stack>
                )}

                {/* ── 등록된 기기 ──────────────────────────────────────── */}
                {devices.length > 0 && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mb: 0.5 }}>
                            등록된 기기 {devices.length}대
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "text.disabled", mb: 1 }}>
                            같은 폰에 브라우저를 두 개 등록하면 알림이 두 번 옵니다. 안 쓰는 건 해지하세요.
                        </Typography>
                        <Stack spacing={0.5}>
                            {devices.map((d) => (
                                <Stack key={d.id} direction="row" alignItems="center" spacing={1}
                                       sx={{ px: 1, py: 0.8, borderRadius: 1, bgcolor: "#f8fafc" }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>
                                            {d.deviceLabel}
                                        </Typography>
                                        <Typography sx={{ fontSize: 10.5, color: "text.disabled" }}>
                                            {d.lastSuccessAt
                                                ? `최근 수신 ${dayjs(d.lastSuccessAt).format("MM-DD HH:mm")}`
                                                : "아직 수신 없음"}
                                        </Typography>
                                    </Box>
                                    {!d.lastSuccessAt && (
                                        <Chip label="확인 필요" size="small"
                                              sx={{ height: 18, fontSize: 10 }} />
                                    )}
                                    <IconButton size="small" onClick={() => removeDevice(d.id)}>
                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Stack>
                            ))}
                        </Stack>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

/** 폰이 열 주소. 페어링 코드는 QR 안에만 있고 화면에 문자로 노출하지 않는다. */
function pairingUrl(code) {
    return `${window.location.origin}/m/setup?c=${encodeURIComponent(code)}`;
}

function expiryText(expiresAt) {
    if (!expiresAt) return "";
    const diffSec = dayjs(expiresAt).diff(dayjs(), "second");
    if (diffSec <= 0) return "만료됨 — 새로 만들어 주세요";
    return `${Math.ceil(diffSec / 60)}분 내 유효`;
}

function readError(e, fallback) {
    return e?.response?.data?.message || e?.message || fallback;
}

function Notice({ tone, children }) {
    const palette = {
        ok:    { bg: "#ecfdf5", fg: "#065f46" },
        warn:  { bg: "#fffbeb", fg: "#92400e" },
        error: { bg: "#fef2f2", fg: "#991b1b" },
    }[tone] || { bg: "#f1f5f9", fg: "#334155" };

    return (
        <Box sx={{ bgcolor: palette.bg, color: palette.fg, borderRadius: 1,
                   px: 1.5, py: 1, fontSize: 12.5, mb: 1.5 }}>
            {children}
        </Box>
    );
}
